export type EntityType = "MASJID" | "INDIVIDUAL" | "ORGANIZATION" | "ARCHIVE" | "END_USER" | "ADMIN";

export type FieldType = "string" | "number" | "boolean" | "date" | "enum";

export type FilterOperator =
  | "equals"
  | "notEquals"
  | "contains"
  | "notContains"
  | "greaterThan"
  | "lessThan"
  | "greaterOrEqual"
  | "lessOrEqual"
  | "isNull"
  | "isNotNull"
  | "inList"
  | "notInList"
  | "regexMatch";

export interface FieldDefinition {
  key: string;
  label: string;
  type: FieldType;
  operators: FilterOperator[];
  enumValues?: string[];
  nested?: boolean;
}

export interface FilterCondition {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string | number | boolean | string[] | null;
  value2?: string | number | null; // For between operations
}

export interface FilterGroup {
  id: string;
  logic: "AND" | "OR";
  conditions: FilterCondition[];
  groups: FilterGroup[];
}

export interface FilterState {
  rootGroup: FilterGroup;
}

export const MASJID_FIELDS: FieldDefinition[] = [
  { key: "masjidName", label: "Masjid Name", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "address", label: "Address", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "isVerified", label: "Is Verified", type: "boolean", operators: ["equals"] },
  { key: "contact", label: "Contact", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "createdAt", label: "Created At", type: "date", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
];

// Individual filter fields aligned with DataGrid columns; status, isAssigned, updatedAt removed (same as Masjid).
export const INDIVIDUAL_FIELDS: FieldDefinition[] = [
  { key: "name", label: "Name", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "individualType", label: "Type", type: "enum", operators: ["equals", "notEquals", "inList", "notInList", "isNull", "isNotNull"], enumValues: ["HAFIZ", "MUFTI", "QAZI"] },
  { key: "contact", label: "Contact", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "address", label: "Address", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "isVerified", label: "Verification Status", type: "boolean", operators: ["equals"] },
  { key: "followers", label: "Followers", type: "number", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
  { key: "subscribers", label: "Subscribers", type: "number", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
  { key: "createdAt", label: "Created At", type: "date", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
];

export const ORGANIZATION_FIELDS: FieldDefinition[] = [
  { key: "name", label: "Name", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "address", label: "Address", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "contact", label: "Contact", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "isFreezed", label: "Is Freezed", type: "boolean", operators: ["equals"] },
  { key: "isVerified", label: "Is Verified", type: "boolean", operators: ["equals"] },
  { key: "organizationType", label: "Organization Type", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "inList", "notInList", "isNull", "isNotNull"] },
  { key: "followers", label: "Followers", type: "number", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
  { key: "subscribers", label: "Subscribers", type: "number", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
  { key: "createdAt", label: "Created At", type: "date", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
  { key: "updatedAt", label: "Updated At", type: "date", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
];

// Archive (deleted users/entities) - client-side filtering
export const ARCHIVE_FIELDS: FieldDefinition[] = [
  { key: "archiveType", label: "Archive Type", type: "string", operators: ["equals", "notEquals", "inList", "notInList", "isNull", "isNotNull"] },
  { key: "originalType", label: "Original Type", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "inList", "notInList", "isNull", "isNotNull"] },
  { key: "reason", label: "Reason", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "inList", "notInList", "isNull", "isNotNull"] },
  { key: "deletedByEmail", label: "Deleted By Email", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "deletedByRole", label: "Deleted By Role", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "inList", "notInList", "isNull", "isNotNull"] },
  { key: "deletedAt", label: "Deleted At", type: "date", operators: ["equals", "notEquals", "greaterThan", "lessThan", "greaterOrEqual", "lessOrEqual", "isNull", "isNotNull"] },
  { key: "isRestored", label: "Is Restored", type: "boolean", operators: ["equals"] },
];

// End users (app users) - client-side filtering
export const END_USER_FIELDS: FieldDefinition[] = [
  { key: "name", label: "Name", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "userName", label: "User Name", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "email", label: "Email", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "contact", label: "Contact", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "role", label: "Role", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "inList", "notInList", "isNull", "isNotNull"] },
  { key: "isVerified", label: "Is Verified", type: "boolean", operators: ["equals"] },
  { key: "isApproved", label: "Is Approved", type: "boolean", operators: ["equals"] },
  { key: "ownershipType", label: "Ownership Type", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "inList", "notInList", "isNull", "isNotNull"] },
];

// Admins (internal/external) - client-side filtering
export const ADMIN_FIELDS: FieldDefinition[] = [
  { key: "name", label: "Name", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "email", label: "Email", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "contact", label: "Contact", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "isNull", "isNotNull", "regexMatch"] },
  { key: "role", label: "Role", type: "string", operators: ["equals", "notEquals", "contains", "notContains", "inList", "notInList", "isNull", "isNotNull"] },
  { key: "isVerified", label: "Is Verified", type: "boolean", operators: ["equals"] },
  { key: "isFreezed", label: "Is Freezed", type: "boolean", operators: ["equals"] },
];

export const getFieldsForEntity = (entityType: EntityType): FieldDefinition[] => {
  switch (entityType) {
    case "MASJID":
      return MASJID_FIELDS;
    case "INDIVIDUAL":
      return INDIVIDUAL_FIELDS;
    case "ORGANIZATION":
      return ORGANIZATION_FIELDS;
    case "ARCHIVE":
      return ARCHIVE_FIELDS;
    case "END_USER":
      return END_USER_FIELDS;
    case "ADMIN":
      return ADMIN_FIELDS;
    default:
      return [];
  }
};

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: "Equals",
  notEquals: "Not Equals",
  contains: "Contains",
  notContains: "Not Contains",
  greaterThan: "Greater Than",
  lessThan: "Less Than",
  greaterOrEqual: "Greater or Equal",
  lessOrEqual: "Less or Equal",
  isNull: "Is Null",
  isNotNull: "Is Not Null",
  inList: "In List",
  notInList: "Not In List",
  regexMatch: "Regex Match",
};



